import { AdminApi } from "../../service";
import { trackPromise } from "react-promise-tracker";
import {
  BaseTaskDefinitionDTO,
  ModelType,
  ScooterAllocatedDto,
  ScooterDto,
  ScooterImeiDto,
  ScooterTaskDefinitionBulkTargetPostDTO,
  ScooterTaskDefinitionTargetPostDTO,
  ScooterTaskTargetDTO,
  ScooterTaskTargetPostDTO,
  TaskDefinitionDTO,
  TaskTargetVehicleDTO,
  VehicleType,
  VehicleListV2Params,
  VehicleListV2Response,
  VehicleListV2Counts,
  VehicleListV2LastRides,
  VehicleListV2SelectionRequest,
  VehicleListV2SelectionResponse,
  VehicleListV2ExportResponse,
  VehicleListV2MapResponse,
  VehicleListV2BulkResponse,
} from "./scooter.interface";

const PREFIX = "scooters";

export async function getVehiclesV2(
  params: VehicleListV2Params,
  trackLoading = true
): Promise<VehicleListV2Response> {
  const request = (params.lastRideAtByVehicleId !== undefined || params.belongingIdsRaw !== undefined
    ? AdminApi.post<VehicleListV2Response>("v2/vehicles", params)
    : AdminApi.get<VehicleListV2Response>("v2/vehicles", {
    params,
    paramsSerializer: (values) => {
      const searchParams = new URLSearchParams();
      Object.entries(values).forEach(([key, value]) => {
        if (value === undefined || value === null || value === "") return;
        if (Array.isArray(value)) {
          value.forEach((item) => searchParams.append(key, String(item)));
          return;
        }
        searchParams.append(key, String(value));
      });
      return searchParams.toString();
    },
  })).then((resp) => resp.data);
  return await (trackLoading ? trackPromise(request) : request);
}

export async function getVehicleCountsV2(): Promise<VehicleListV2Counts> {
  return await AdminApi.get<VehicleListV2Counts>("v2/vehicles/counts").then((resp) => resp.data);
}

export async function getVehicleLastRidesV2(vehicleIds: string[]): Promise<VehicleListV2LastRides> {
  return await AdminApi.get<VehicleListV2LastRides>("v2/vehicles/last-rides", {
    params: { vehicleIds },
    paramsSerializer: () => {
      const params = new URLSearchParams();
      vehicleIds.forEach((id) => params.append("vehicleIds", id));
      return params.toString();
    },
  }).then((resp) => resp.data);
}

export async function exportVehiclesV2(
  request: VehicleListV2SelectionRequest
): Promise<VehicleListV2ExportResponse> {
  return await trackPromise(
    AdminApi.post<VehicleListV2ExportResponse>("v2/vehicles/export", request)
      .then((resp) => resp.data)
  );
}

export async function resolveVehicleSelectionIdsV2(
  request: VehicleListV2SelectionRequest
): Promise<VehicleListV2SelectionResponse> {
  return await trackPromise(
    AdminApi.post<VehicleListV2SelectionResponse>("v2/vehicles/selection-ids", request)
      .then((resp) => resp.data)
  );
}

export async function getVehiclesMapV2(
  request: VehicleListV2SelectionRequest
): Promise<VehicleListV2MapResponse> {
  return await trackPromise(
    AdminApi.post<VehicleListV2MapResponse>("v2/vehicles/map", request)
      .then((resp) => resp.data)
  );
}

export async function updateVehicleStatusV2(
  request: VehicleListV2SelectionRequest & { type: string; title?: string }
): Promise<VehicleListV2BulkResponse> {
  return await trackPromise(
    AdminApi.post<VehicleListV2BulkResponse>("v2/vehicles/bulk/status", request)
      .then((resp) => resp.data)
  );
}

export async function updateVehicleWorkTargetsV2(
  request: VehicleListV2SelectionRequest & {
    operation: "SET" | "CLEAR";
    workDefinitionId?: number;
    expiredAtTs?: number;
  }
): Promise<VehicleListV2BulkResponse> {
  return await trackPromise(
    AdminApi.post<VehicleListV2BulkResponse>("v2/vehicles/bulk/work-targets", request)
      .then((resp) => resp.data)
  );
}

export async function updateVehicleDynamicPriceV2(
  request: VehicleListV2SelectionRequest & { ratio: number; activeHours: number }
): Promise<VehicleListV2BulkResponse> {
  return await trackPromise(
    AdminApi.post<VehicleListV2BulkResponse>("v2/vehicles/bulk/dynamic-price", request)
      .then((resp) => resp.data)
  );
}

export async function getScooterCount(vehicleTypes) {
  return await trackPromise(
    AdminApi.get(`${PREFIX}/count`, {
      params: {
        vehicleTypes,
      },
    }).then((resp) => resp.data)
  );
}

export async function getScootersByVehicleType(args: {
  vehicleTypes: VehicleType[];
  offset: number;
  limit: number;
}): Promise<ScooterDto[]> {
  const { vehicleTypes, limit, offset } = args;
  return await getScooters(vehicleTypes.join(","), offset, limit);
}

export async function getScooters(
  vehicleTypes: string,
  offset: number,
  limit: number
) {
  return await trackPromise(
    AdminApi.get(`${PREFIX}`, {
      params: {
        vehicleTypes,
        offset,
        limit,
      },
    }).then((resp) => resp.data)
  );
}

export async function getUsedScooterCount() {
  return await trackPromise(
    AdminApi.get(`${PREFIX}/count`, {
      params: {
        used: true,
      },
    }).then((resp) => resp.data)
  );
}

export async function getUsedScooterList({ offset, limit }) {
  return await trackPromise(
    AdminApi.get(`${PREFIX}`, {
      params: {
        used: true,
        offset: offset,
        limit: limit,
      },
    }).then((resp) => resp.data)
  );
}

export async function getScooterImeis(): Promise<ScooterImeiDto[]> {
  return await trackPromise(
    AdminApi.get<ScooterImeiDto[]>(`${PREFIX}/imeis`).then((resp) => resp.data)
  );
}

export async function getOnlineScooter(): Promise<{ [key: string]: boolean }> {
  return await trackPromise(
    AdminApi.get(`${PREFIX}/online`).then((resp) => resp.data)
  );
}

export async function getInspectScooter() {
  return await trackPromise(
    AdminApi.get(`${PREFIX}/inspect`).then((resp) => resp.data)
  );
}

//작업 대상 조회 (만료 안된)
export async function getTaskTargetScooters(): Promise<TaskTargetVehicleDTO[]> {
  return await trackPromise(
    AdminApi.get(`collector/work-targets?type=AVAILABLE`).then(
      (resp) => resp.data
    )
  );
}

//TODO: 기기타입 추가
export async function getLastRideTime() {
  return await trackPromise(
    AdminApi.get(`${PREFIX}`, {
      params: {
        include: "lastRideTime",
      },
    }).then((resp) => resp.data)
  );
}

export async function getScooter(imei) {
  return await trackPromise(
    AdminApi.get(`${PREFIX}/${imei}`).then((resp) => resp.data)
  );
}

export async function getScooterHistories(imei) {
  return await trackPromise(
    AdminApi.get(`${PREFIX}/${imei}/histories`).then((resp) => resp.data)
  );
}

export async function getScooterRidingList(imei) {
  return await trackPromise(
    AdminApi.get(`${PREFIX}/riding?imei=${imei}`).then((resp) => resp.data)
  );
}

export async function createScooterHistories(imei, request) {
  const result = await AdminApi.post(`${PREFIX}/${imei}/histories`, request);
  return result;
}

export async function updateScooterComment(imei, request) {
  const result = await AdminApi.put(`${PREFIX}/${imei}/comment`, request);
  return result;
}

export async function updateScooterShortImei(imei, request) {
  return await trackPromise(
    AdminApi.put(`${PREFIX}/${imei}/short-imei`, request).then(
      (resp) => resp.data
    )
  ).catch((e) => alert(e.response.data.message));
}

export async function updateScooterMac(imei, request) {
  return await trackPromise(
    AdminApi.put(`${PREFIX}/${imei}/mac`, request).then((resp) => resp.data)
  );
}

export async function updateScooterBand(imei, request) {
  return await trackPromise(
    AdminApi.put(`${PREFIX}/${imei}/band`, request).then((resp) => resp.data)
  );
}

export async function updateScooterLocation(imei, request) {
  return await trackPromise(
    AdminApi.put(`${PREFIX}/${imei}/location`, request).then(
      (resp) => resp.data
    )
  );
}

export async function updateScooterSim(imei, request) {
  return await trackPromise(
    AdminApi.put(`${PREFIX}/${imei}/sim`, request).then((resp) => resp.data)
  );
}

export async function getAllocatedTime(): Promise<ScooterAllocatedDto[]> {
  return await trackPromise(
    AdminApi.get(`${PREFIX}?include=allocatedTime`).then((resp) => resp.data)
  );
}

export async function getScooterRecords(imei, startAt, endAt) {
  return await trackPromise(
    AdminApi.get(`${PREFIX}/${imei}/position?startAt=${startAt}&endAt=${endAt}`)
      .then((resp) => resp.data)
      .catch((e) => {
        return alert(e.response.data.message);
      })
  );
}

export async function controlScooter({ imei, type, request }) {
  return await trackPromise(
    AdminApi.put(`${PREFIX}/${imei}/${type}`, request).then((resp) => resp.data)
  );
}

export async function setMinwonScooterEmergency(imei, request) {
  return await trackPromise(
    AdminApi.put(`${PREFIX}/${imei}/minwon/emergency`, request).then(
      (resp) => resp.data
    )
  );
}

export async function postInspectScoots(shortImeis) {
  return await trackPromise(
    AdminApi.post(`${PREFIX}/inspect`, {
      shortImeis: shortImeis,
    }).then((resp) => resp.data)
  );
}
//특정기기 작업상태 조회
export async function getTaskStatusByVehicleId(
  vehicleId: string
): Promise<TaskTargetVehicleDTO[]> {
  return await trackPromise(
    AdminApi.get(`collector/work-targets?vehicleId=${vehicleId}`).then(
      (resp) => resp.data
    )
  );
}

//작업정의 전체 조회
export async function getTaskDefinitions(trackLoading = true): Promise<TaskDefinitionDTO[]> {
  const request = AdminApi.get<TaskDefinitionDTO[]>(`collector/work-definitions`).then((resp) => resp.data);
  return await (trackLoading ? trackPromise(request) : request);
}
//작업정의 타입 추가, 대문자 + _형식의 ENUM이 name
export async function postTaskDefinition(props: BaseTaskDefinitionDTO) {
  return await trackPromise(
    AdminApi.post(`collector/work-definitions`, props).then((resp) => resp.data)
  );
}

export async function postTaskDefinitionTargetScooters(
  request: ScooterTaskDefinitionTargetPostDTO
) {
  return await trackPromise(
    AdminApi.post(`collector/work-targets`, request).then((resp) => resp.data)
  );
}

// targetIds로도 가능함
// 근데 여기선 쓸데없어서 그냥 안함
export async function postResetTaskDefinitionBulkTarget({
  targetVehicleIds,
}: {
  targetVehicleIds: string[];
}) {
  const result = await AdminApi.post(`collector/work-targets/bulk/clear`, {
    targetVehicleIds: targetVehicleIds,
  });
  if (result.status !== 200) return alert("에러발생");
  return result.data;
}

export async function postTaskDefinitionBulkTarget(
  request: ScooterTaskDefinitionBulkTargetPostDTO
) {
  return await trackPromise(
    AdminApi.post(`collector/work-targets/bulk`, request).then(
      (resp) => resp.data
    )
  );
}

export async function getVehiclesForMap() {
  const result = await AdminApi.get(`${PREFIX}/map`);

  if (result.status !== 200) return alert("에러발생");

  return result.data;
}

export async function putVehicleDynamicPricing({
  activeHours,
  imei,
  ratio,
}: {
  imei: string;
  ratio: number;
  activeHours: number;
}) {
  const result = await AdminApi.put(`vehicles/${imei}/dynamic-price`, {
    ratio,
    activeHours,
  });

  return result;
}

export default {
  getScooters,
  getScooterCount,
  getUsedScooterList,
  getUsedScooterCount,
  getScooterImeis,
  getOnlineScooter,
  getInspectScooter,
  getTaskTargetScooters,
  getVehiclesForMap,

  getScooter,
  getScooterHistories,
  getScooterRidingList,
  getScooterRecords,

  getLastRideTime,
  getAllocatedTime,

  updateScooterComment,
  updateScooterShortImei,
  updateScooterBand,
  updateScooterLocation,
  updateScooterSim,
  updateScooterMac,

  createScooterHistories,

  controlScooter,

  setMinwonScooterEmergency,

  getTaskDefinitions,
  postTaskDefinition,
  postTaskDefinitionTargetScooters,
  postTaskDefinitionBulkTarget,
  postResetTaskDefinitionBulkTarget,
  getTaskStatusByVehicleId,
};
